import CardWrapper from '@components/UI/CardWrapper';
import { useBitrix } from '@context/useBitrixUser/useBitrix';
import { zodResolver } from '@hookform/resolvers/zod';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { companyProfileService } from '@services/requests/companyProfileService';
import { AccessData } from '@services/requests/companyProfileService/types/accessData';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ChangeNotification } from '../../ChangeNotification';
import { passwordFormSchema } from './schemas';
import { PasswordModuleStyledContainer } from './StyledComponents';
import { PasswordFormFields } from './types';

interface PasswordModuleProps {
  accessData: AccessData;
}

function PasswordModule({ accessData }: PasswordModuleProps) {
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [isChangeNotificationOpen, setIsChangeNotificationOpen] = useState<boolean>(false);

  const { useBitrixUser } = useBitrix();
  const [{ bitrixUser }] = useBitrixUser().useState();

  const form = useForm<PasswordFormFields>({
    resolver: zodResolver(passwordFormSchema),
  });

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors },
  } = form;

  const submitPasswordFormHandler = async (data: PasswordFormFields) => {
    const result = await companyProfileService().SaveAccess(bitrixUser.id, data);

    if (!result.success) {
      setError('root', { message: result?.errors[0] ?? '' });
      return;
    }

    setIsChangeNotificationOpen(true);
    reset();
  };

  return (
    <>
      <PasswordModuleStyledContainer>
        <form id="password-form" onSubmit={handleSubmit(submitPasswordFormHandler)}>
          <CardWrapper sx={{ padding: '25px 40px' }} className="card">
            <Typography variant={'h3'} className={'module-title'}>
              {accessData.section.caption}:
            </Typography>
            <Controller
              control={control}
              name="currentPassword"
              render={({ field: { onChange, value, ref, onBlur, name } }) => (
                <TextField
                  fullWidth
                  className="comment-field"
                  size={'medium'}
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  type={showCurrentPassword ? 'text' : 'password'}
                  label={accessData.section.fields.password.title}
                  variant="outlined"
                  value={value || ''}
                  error={!!errors.currentPassword}
                  InputProps={{
                    classes: { input: 'browser-default' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showCurrentPassword ? 'hide the password' : 'display the password'
                          }
                          onClick={() => setShowCurrentPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showCurrentPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="newPassword"
              render={({ field: { onChange, value, ref, onBlur, name } }) => (
                <TextField
                  fullWidth
                  className="comment-field"
                  size={'medium'}
                  type={showNewPassword ? 'text' : 'password'}
                  label={accessData.section.fields.new_password.title}
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  variant="outlined"
                  value={value || ''}
                  error={!!errors.newPassword || !!errors.root}
                  helperText={errors.root?.message}
                  InputProps={{
                    classes: { input: 'browser-default' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showNewPassword ? 'hide the password' : 'display the password'
                          }
                          onClick={() => setShowNewPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showNewPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </CardWrapper>
        </form>
      </PasswordModuleStyledContainer>

      <ChangeNotification
        needReviewType={false}
        isOpen={isChangeNotificationOpen}
        onClose={() => setIsChangeNotificationOpen(false)}
      />
    </>
  );
}
export { PasswordModule };
